import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { LinkButton } from './styles';

// Inline per-outcome notices. `NoticeText` colours a line by tone through the theme and carries
// the right live-region role (the SnapshotOutput treatment); `OutcomePanel` is a bordered list of
// them that stays until dismissed — for the queue's "decided, but not saved for the issuer"
// notes, which are the demo's carry-over story and must outlive a list refresh.

export const NOTICE_TONES = { POSITIVE: 'positive', WARNING: 'warning', ERROR: 'error', NEUTRAL: 'neutral' };

const Toned = styled.div`
  ${({ theme, $tone }) =>
    $tone === 'neutral' || !theme.themeColors[$tone] ? '' : `color: ${theme.themeColors[$tone]};`}

  .base-text {
    ${({ theme, $tone }) =>
      $tone === 'neutral' || !theme.themeColors[$tone] ? '' : `color: ${theme.themeColors[$tone]};`}
  }
`;

export const NoticeText = ({ tone = NOTICE_TONES.NEUTRAL, children, small = false }) => {
  const Text = small ? Body7 : Body5;
  return (
    <Toned $tone={tone} role={tone === NOTICE_TONES.ERROR ? 'alert' : 'status'}>
      <Text>{children}</Text>
    </Toned>
  );
};

const Panel = styled.div`
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === NOTICE_TONES.ERROR
        ? theme.themeColors.error
        : $tone === NOTICE_TONES.WARNING
        ? theme.themeColors.warning
        : theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 12px 16px;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ItemList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
`;

const Item = styled.li`
  margin: 4px 0;
`;

// `items` is `[{ key, text, tone? }]`. The panel's own tone is the strongest of its items.
export const OutcomePanel = ({ title, items = [], onDismiss, tone }) => {
  if (!items.length) return null;
  const panelTone =
    tone ||
    (items.some((i) => i.tone === NOTICE_TONES.ERROR)
      ? NOTICE_TONES.ERROR
      : items.some((i) => i.tone === NOTICE_TONES.WARNING)
      ? NOTICE_TONES.WARNING
      : NOTICE_TONES.NEUTRAL);
  return (
    <Panel $tone={panelTone} role={panelTone === NOTICE_TONES.ERROR ? 'alert' : 'status'}>
      <PanelHead>
        <Body5 bold>{title}</Body5>
        {onDismiss && <LinkButton onClick={onDismiss}>Dismiss</LinkButton>}
      </PanelHead>
      <ItemList>
        {items.map((item) => (
          <Item key={item.key}>
            <NoticeText tone={item.tone || NOTICE_TONES.NEUTRAL}>{item.text}</NoticeText>
          </Item>
        ))}
      </ItemList>
      {items.length > 0 && items.some((i) => i.action) && (
        <Container top={8} row flexWrap gap={8}>
          {items
            .filter((i) => i.action)
            .map((i) => (
              <React.Fragment key={`${i.key}-action`}>{i.action}</React.Fragment>
            ))}
        </Container>
      )}
    </Panel>
  );
};
